import { useEffect, useId, useRef, useState } from "react";
import { AiExamples } from "./AiExamples";
import "./ai-showcase.css";
import "./agency-connect.css";

const installCommand = "agency plugin install market:easel@playground --engine copilot";

function Command({ text }: { text: string }) {
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");
  useEffect(() => {
    if (status !== "copied") return;
    const timer = window.setTimeout(() => setStatus("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [status]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setStatus("copied");
    } catch {
      setStatus("error");
    }
  }
  return (
    <div className="agency-command">
      <div className="agency-command-surface ai-copy-box">
        <pre className="ai-copy-content">
          <code>{text}</code>
        </pre>
        <button
          type="button"
          className="ai-text-link"
          onClick={() => void copy()}
          aria-live="polite"
        >
          {status === "copied" ? "Copied!" : "Copy command"}
        </button>
      </div>
      {status === "error" && (
        <p role="alert">Copy failed. Select the command and copy it, or retry.</p>
      )}
    </div>
  );
}

export default function AgencyConnectDialog({
  onClose,
  onBack,
  initialStep = 1,
}: {
  onClose: () => void;
  onBack: () => void;
  initialStep?: 1 | 2;
}) {
  const [step, setStep] = useState<1 | 2>(initialStep);
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus();
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", close, true);
    return () => {
      document.removeEventListener("keydown", close, true);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [onClose]);
  return (
    <div className="ai-hub-overlay" onClick={onClose}>
      <div
        className="ai-hub agency-setup"
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ai-setup-header">
          <button
            type="button"
            className="ai-step-nav"
            aria-label="Back"
            title="Back to Create with AI"
            onClick={onBack}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m14 7-5 5 5 5" />
            </svg>
          </button>
          <h2 id={titleId}>{step === 1 ? "Set up Agency" : "Create and edit drawings"}</h2>
        </div>
        <ol className="ai-stepper" aria-label="Setup progress">
          {(
            [
              { value: 1, label: "Install & start" },
              { value: 2, label: "Create & edit" },
            ] as const
          ).map(({ value, label }) => (
            <li key={value}>
              <button
                type="button"
                aria-label={`${value}. ${label}`}
                aria-current={step === value ? "step" : undefined}
                onClick={() => setStep(value)}
              >
                <span className="ai-stepper-number">{value}</span>
                <span className="ai-stepper-label">{label}</span>
              </button>
            </li>
          ))}
        </ol>
        <div className="ai-guided-flow">
          {step === 1 ? (
            <>
              <p className="agency-prerequisite">
                New to Agency? Install{" "}
                <a href="https://aka.ms/agency" target="_blank" rel="noreferrer">
                  Agency
                </a>{" "}
                first.
              </p>
              <section>
                <h3>Install Easel in your terminal.</h3>
                <Command text={installCommand} />
              </section>
              <section className="agency-launch">
                <h3>Start Copilot in the same terminal.</h3>
                <Command text="agency copilot" />
              </section>
              <div className="agency-signin">
                <strong>In Copilot, sign in to Excalidraw-MS.</strong>
                <Command text="/mcp auth excalidraw-ms" />
                <p>Complete Microsoft sign-in with your work account.</p>
              </div>
            </>
          ) : (
            <AiExamples agency />
          )}
        </div>
        <p className="agency-note">Requires Agency CLI with a Microsoft work account.</p>
      </div>
    </div>
  );
}
